<template>
    <vue-modal-2 
      name="ModalSheetConfig" 
      :headerOptions="{title: 'Configuration Google Sheet'}" 
      :noFooter="false" 
      modalSize="xl" 
      @on-close="closeModal">
      
      <div class="bg-white rounded-lg p-6">
        
        <!-- Alert Info -->
        <div class="bg-blue-100 border-l-4 border-blue-500 p-4 mb-6">
          <div class="flex">
            <div class="flex-shrink-0">
              <svg class="h-5 w-5 text-blue-500" fill="currentColor" viewBox="0 0 20 20">
                <path fill-rule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clip-rule="evenodd"/>
              </svg>
            </div>
            <div class="ml-3">
              <p class="text-sm text-blue-700">
                <strong>📊 Import automatique depuis Google Sheets</strong><br>
                Synchronisez vos commandes automatiquement et voyez le statut mis à jour dans votre sheet.
              </p>
            </div>
          </div>
        </div>
  
        <!-- Enable/Disable Switch -->
        <div class="mb-6 flex items-center justify-between p-4 bg-gray-100 rounded-lg">
          <div>
            <h3 class="text-lg font-medium text-gray-900">Activer la synchronisation</h3>
            <p class="text-sm text-gray-600">Activer l'importation automatique depuis Google Sheets</p>
          </div>
          
          <div class="relative">
            <input 
              type="checkbox" 
              id="toggle-enabled"
              v-model="config.enabled" 
              class="sr-only">
            <label 
              for="toggle-enabled"
              class="toggle-switch cursor-pointer"
              :class="{'toggle-active': config.enabled}">
              <span class="toggle-slider"></span>
            </label>
          </div>
        </div>
  
        <div v-if="config.enabled">
          
          <!-- Instructions détaillées -->
          <div class="bg-blue-50 border-l-4 border-blue-400 p-4 mb-6">
            <h4 class="text-sm font-semibold text-blue-800 mb-3">📝 Comment configurer en 2 étapes</h4>

            <!-- Étape 1: Partager avec le Service Account -->
            <div class="mb-4">
              <h5 class="text-sm font-semibold text-blue-700 mb-2 flex items-center">
                <span class="bg-blue-600 text-white rounded-full w-6 h-6 flex items-center justify-center text-xs mr-2">1</span>
                Partager votre Google Sheet avec notre système
              </h5>
              <ol class="text-sm text-blue-700 space-y-1 ml-8 list-decimal">
                <li>Ouvrez votre Google Sheet</li>
                <li>Cliquez sur <strong>"Partager"</strong> (en haut à droite)</li>
                <li>Dans le champ "Ajouter des personnes et des groupes", collez cet email:</li>
              </ol>
              
              <!-- Email à copier avec animation -->
              <div class="mt-3 bg-white border-2 border-blue-400 rounded-lg p-4 shadow-sm">
                <div class="flex items-center justify-between">
                  <div class="flex-1 mr-3">
                    <p class="text-xs font-semibold text-gray-700 mb-1">📧 Email du système:</p>
                    <code class="text-sm font-mono text-blue-800 break-all block bg-blue-50 p-2 rounded">
                      {{ serviceAccountEmail }}
                    </code>
                  </div>
                  <button 
                    @click="copyServiceAccountEmail"
                    class="flex-shrink-0 px-4 py-2 bg-blue-600 text-white text-sm font-medium rounded-lg hover:bg-blue-700 transition flex items-center">
                    <svg v-if="!emailCopied" class="w-4 h-4 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z"/>
                    </svg>
                    <svg v-else class="w-4 h-4 mr-1" fill="currentColor" viewBox="0 0 20 20">
                      <path fill-rule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clip-rule="evenodd"/>
                    </svg>
                    {{ emailCopied ? 'Copié!' : 'Copier' }}
                  </button>
                </div>
              </div>
              
              <ol start="4" class="text-sm text-blue-700 space-y-1 ml-8 list-decimal mt-3">
                <li>Sélectionnez le rôle: <strong>"Éditeur"</strong> (pas "Lecteur"!)</li>
                <li><strong>Décochez</strong> "Notifier les personnes" (pas besoin)</li>
                <li>Cliquez sur <strong>"Envoyer"</strong></li>
              </ol>
              
          <!-- Screenshots / Images explicatives -->
            <div class="mt-3 grid grid-cols-1 md:grid-cols-2 gap-3">
            <!-- Image 1: Bouton Partager -->
            <div class="bg-white border border-blue-200 rounded p-3">
                <p class="text-xs font-semibold text-gray-700 mb-2">Étape 1 : Cliquer sur "Partager"</p>
                <div class="bg-gray-50 rounded overflow-hidden flex items-center justify-center p-2" style="min-height: 180px;">
                <expandable-image 
                    class="api-image-screenshot" 
                    src="https://storeno.b-cdn.net/colismartgo-v1/11-2025/1762188053800.png" 
                    alt="Cliquer sur Partager dans Google Sheets" 
                />
                </div>
            </div>
            
            <!-- Image 2: Ajouter l'email -->
            <div class="bg-white border border-blue-200 rounded p-3">
                <p class="text-xs font-semibold text-gray-700 mb-2">Étape 2 : Ajouter l'email en "Éditeur"</p>
                <div class="bg-gray-50 rounded overflow-hidden flex items-center justify-center p-2" style="min-height: 180px;">
                <expandable-image 
                    class="api-image-screenshot" 
                    src="https://storeno.b-cdn.net/colismartgo-v1/11-2025/1762188354913.png" 
                    alt="Ajouter l'email en Éditeur" 
                />
                </div>
            </div>
            </div>
              
              <!-- Alertes importantes -->
              <div class="mt-3 space-y-2">
                <div class="flex items-start bg-green-50 border border-green-200 rounded p-2">
                  <svg class="w-5 h-5 text-green-600 mr-2 flex-shrink-0 mt-0.5" fill="currentColor" viewBox="0 0 20 20">
                    <path fill-rule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clip-rule="evenodd"/>
                  </svg>
                  <p class="text-xs text-green-800">
                    <strong>Pourquoi "Éditeur" ?</strong> Cela nous permet de lire vos commandes ET de mettre à jour automatiquement le statut (SENT/ERROR) dans votre sheet.
                  </p>
                </div>
                
                <div class="flex items-start bg-yellow-50 border border-yellow-200 rounded p-2">
                  <svg class="w-5 h-5 text-yellow-600 mr-2 flex-shrink-0 mt-0.5" fill="currentColor" viewBox="0 0 20 20">
                    <path fill-rule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clip-rule="evenodd"/>
                  </svg>
                  <p class="text-xs text-yellow-800">
                    <strong>Sécurité:</strong> Cet email est un compte robot automatisé. Nous ne modifions que les colonnes STATUS et SENT_AT. Vos données restent sécurisées.
                  </p>
                </div>
              </div>
            </div>

            <!-- Étape 2: Récupérer Sheet ID -->
            <div class="mb-4">
              <h5 class="text-sm font-semibold text-blue-700 mb-2 flex items-center">
                <span class="bg-blue-600 text-white rounded-full w-6 h-6 flex items-center justify-center text-xs mr-2">2</span>
                Récupérer le Sheet ID et le nom de l'onglet
              </h5>
              <ol class="text-sm text-blue-700 space-y-1 ml-8 list-decimal">
                <li>Copiez l'URL de votre Google Sheet (barre d'adresse)</li>
                <li>Le Sheet ID se trouve entre <code class="bg-white px-1 rounded">/d/</code> et <code class="bg-white px-1 rounded">/edit</code></li>
                <li>Notez le nom de l'onglet (en bas du sheet)</li>
              </ol>
              
              <div class="mt-2 bg-white border border-blue-200 rounded p-3">
                <p class="text-xs font-semibold text-gray-700 mb-1">Exemple d'URL:</p>
                <div class="font-mono text-xs text-gray-600 break-all bg-gray-50 p-2 rounded">
                  https://docs.google.com/spreadsheets/d/<span class="bg-green-200 font-bold px-1">1e2Q4ZGMv616zNx60rjgEzDECdK_B1tTGYE2SNygmhzA</span>/edit?gid=0#gid=0
                </div>
                <p class="text-xs text-gray-600 mt-2">
                  ↑ La partie surlignée en vert est votre <strong>Sheet ID</strong>
                </p>
              </div>
              
              <div class="mt-2 bg-white border border-blue-200 rounded p-3">
                <p class="text-xs font-semibold text-gray-700 mb-1">Exemple d'onglets (en bas du sheet):</p>
                <div class="flex space-x-2 text-xs">
                  <span class="px-2 py-1 bg-gray-200 rounded">Sheet1</span>
                  <span class="px-2 py-1 bg-green-200 font-bold rounded">formatCorrect</span>
                  <span class="px-2 py-1 bg-gray-200 rounded">Archive</span>
                </div>
                <p class="text-xs text-gray-600 mt-2">
                  ↑ Dans cet exemple, le nom du sheet est: <strong>formatCorrect</strong>
                </p>
              </div>
            </div>
          </div>
  
          <!-- Section 1: Sheet ID et Sheet Name -->
          <div class="mb-6">
            <h3 class="text-lg font-semibold mb-4 flex items-center">
              <span class="bg-blue-500 text-white rounded-full w-8 h-8 flex items-center justify-center mr-3">1</span>
              Informations du Google Sheet
            </h3>

            <div class="space-y-4">
              <!-- Sheet ID -->
              <div>
                <label class="block text-sm font-medium text-gray-700 mb-2">
                  Sheet ID <span class="text-red-500">*</span>
                </label>
                <input 
                  v-model="config.sheetId" 
                  type="text" 
                  placeholder="1BxiMVs0XRA5nFMdKvBdBZjgmUUqptlbs74OgvE2upms"
                  class="w-full px-4 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 font-mono text-sm bg-gray-100"
                  :class="{'border-red-500': errors.sheetId, 'border-gray-300': !errors.sheetId}">
                <p class="text-xs text-gray-500 mt-1">
                  L'ID se trouve dans l'URL entre <code>/d/</code> et <code>/edit</code>
                </p>
                <p v-if="errors.sheetId" class="text-xs text-red-500 mt-1">{{ errors.sheetId }}</p>
              </div>

              <!-- Sheet Name -->
              <div>
                <label class="block text-sm font-medium text-gray-700 mb-2">
                  Nom du Sheet/Onglet <span class="text-red-500">*</span>
                </label>
                <input 
                  v-model="config.sheetName" 
                  type="text" 
                  placeholder="formatCorrect"
                  class="w-full px-4 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 bg-gray-100"
                  :class="{'border-red-500': errors.sheetName, 'border-gray-300': !errors.sheetName}">
                <p class="text-xs text-gray-500 mt-1">
                  Le nom exact de l'onglet (sensible à la casse)
                </p>
                <p v-if="errors.sheetName" class="text-xs text-red-500 mt-1">{{ errors.sheetName }}</p>
              </div>

              <!-- Start Row -->
              <div>
                <label class="block text-sm font-medium text-gray-700 mb-2">
                  Ligne de départ
                </label>
                <input 
                  v-model.number="config.startRow" 
                  type="number" 
                  min="2"
                  class="w-full px-4 py-2 border border-gray-300 rounded-lg bg-gray-100">
                <p class="text-xs text-gray-500 mt-1">
                    Commence à la ligne choisie (par défaut 2 — la ligne 1 contient les en-têtes).
                </p>
              </div>
            </div>
          </div>
  
          <!-- Section 2: Column Mapping -->
          <div class="mb-6">
            <h3 class="text-lg font-semibold mb-4 flex items-center">
              <span class="bg-blue-500 text-white rounded-full w-8 h-8 flex items-center justify-center mr-3">2</span>
              Mapping des colonnes
            </h3>
  
            <div class="bg-yellow-100 border-l-4 border-yellow-500 p-4 mb-4">
              <p class="text-sm text-yellow-700">
                <strong>Important:</strong> Indiquez la lettre de colonne correspondant à chaque champ (A, B, C, ... Z, AA, AB, etc.)
              </p>
            </div>
  
            <div class="grid grid-cols-2 md:grid-cols-3 gap-4">
              <div v-for="(field, key) in config.columnMapping" :key="key">
                <label class="block text-sm font-medium text-gray-700 mb-2">
                  {{ formatFieldName(key) }}
                  <span v-if="isRequiredField(key)" class="text-red-500">*</span>
                </label>
                <input 
                  v-model="config.columnMapping[key]" 
                  type="text" 
                  :placeholder="getDefaultColumn(key)"
                  maxlength="5"
                  @input="normalizeColumnInput(key)"
                  class="w-full px-4 py-2 border rounded-lg uppercase focus:outline-none focus:ring-2 focus:ring-blue-500"
                  :class="{'border-red-500': errors[`columnMapping.${key}`], 'border-gray-300': !errors[`columnMapping.${key}`]}">
                <p v-if="errors[`columnMapping.${key}`]" class="text-xs text-red-500 mt-1">
                  {{ errors[`columnMapping.${key}`] }}
                </p>
              </div>
            </div>
          </div>
  
          <!-- Section 3: Preview -->
          <div class="mb-6">
            <h3 class="text-lg font-semibold mb-4 flex items-center">
              <span class="bg-blue-500 text-white rounded-full w-8 h-8 flex items-center justify-center mr-3">3</span>
              Aperçu de la structure
            </h3>
  
            <div class="overflow-x-auto">
              <table class="min-w-full border">
                <thead class="bg-gray-100">
                  <tr>
                    <th v-for="(field, key) in config.columnMapping" :key="key"
                        class="px-3 py-3 text-left text-xs font-medium text-gray-600 uppercase tracking-wider border-r">
                      {{ field || '?' }}
                    </th>
                  </tr>
                </thead>
                <tbody class="bg-white">
                  <tr>
                    <td v-for="(field, key) in config.columnMapping" :key="key"
                        class="px-3 py-4 whitespace-no-wrap text-sm text-gray-900 border-r border-b">
                      {{ formatFieldName(key) }}
                    </td>
                  </tr>
                  <tr class="bg-gray-50">
                    <td v-for="(field, key) in config.columnMapping" :key="key"
                        class="px-3 py-4 whitespace-no-wrap text-sm text-gray-600 border-r italic">
                      {{ getExampleValue(key) }}
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>
          </div>
  
          <!-- Test Connection Button -->
          <div class="mb-6">
            <button 
              @click="testConnection" 
              :disabled="loading"
              class="w-full md:w-auto px-6 py-3 bg-green-600 text-white rounded-lg hover:bg-green-700 focus:outline-none"
              :class="{'opacity-50 cursor-not-allowed': loading}">
              <span v-if="!loading">🔍 Tester la connexion</span>
              <span v-else>⏳ Test en cours...</span>
            </button>
            
            <div v-if="testResult" class="mt-4 p-4 rounded-lg border" 
                 :class="testResult.success ? 'bg-green-50 border-green-200' : 'bg-red-50 border-red-200'">
              <p class="text-sm" :class="testResult.success ? 'text-green-800' : 'text-red-800'">
                {{ testResult.message }}
              </p>
              <div v-if="testResult.preview && testResult.preview.length > 0" class="mt-2 text-xs text-gray-600">
                <strong>Aperçu des données ({{ Math.min(testResult.preview.length, 3) }} premières lignes):</strong>
                <div class="mt-2 space-y-2">
                  <div v-for="(row, idx) in testResult.preview.slice(0, 3)" :key="idx" 
                       class="p-2 bg-white rounded border">
                    <strong>Ligne {{ idx + 1 }}:</strong>
                    <pre class="text-xs mt-1 overflow-x-auto">{{ JSON.stringify(row, null, 2) }}</pre>
                  </div>
                </div>
              </div>
            </div>
          </div>
  
          <!-- Stats -->
          <div v-if="currentUser.sheetConfig && currentUser.sheetConfig.lastSync" 
               class="grid grid-cols-3 gap-4 mb-6">
            <div class="bg-blue-100 p-4 rounded-lg">
              <p class="text-xs text-gray-600">Dernière sync</p>
              <p class="text-lg font-semibold text-blue-700">{{ formatDate(currentUser.sheetConfig.lastSync) }}</p>
            </div>
            <div class="bg-green-100 p-4 rounded-lg">
              <p class="text-xs text-gray-600">Commandes importées</p>
              <p class="text-lg font-semibold text-green-700">{{ currentUser.sheetConfig.totalOrders || 0 }}</p>
            </div>
            <div class="bg-yellow-100 p-4 rounded-lg">
              <p class="text-xs text-gray-600">Statut</p>
              <p class="text-lg font-semibold text-yellow-700">{{ currentUser.sheetConfig.enabled ? 'Actif' : 'Inactif' }}</p>
            </div>
          </div>
  
        </div>
  
      </div>
  
      <!-- Footer -->
      <template v-slot:footer>
        <div class="flex justify-end px-6 py-4 border-t" style="gap: 12px;">
          <button 
            @click="closeModal" 
            class="px-6 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-100 focus:outline-none">
            Annuler
          </button>
          <button 
            @click="saveConfig" 
            :disabled="saving"
            class="px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 focus:outline-none"
            :class="{'opacity-50 cursor-not-allowed': saving}">
            <span v-if="!saving">💾 Enregistrer</span>
            <span v-else>⏳ Enregistrement...</span>
          </button>
        </div>
      </template>
  
    </vue-modal-2>
  </template>

<script>
export default {
  name: 'SheetConfigModal',
  
  props: {
    currentUser: {
      type: Object,
      required: true
    }
  },

  data() {
    return {
      serviceAccountEmail: 'sheets-bot@projects-shipping-sheets.iam.gserviceaccount.com', // 🆕
      emailCopied: false, // 🆕
      config: {
        enabled: false,
        sheetId: '',
        sheetName: '',
        startRow: 2,
        columnMapping: {
          ORDER_ID: 'A',
          PRODUCT_ID: 'B',
          CUSTOMER_NAME: 'C',
          PHONE: 'D',
          CITY: 'E',
          TOTAL: 'F',
          QUANTITY: 'G',
          STORE_NAME: 'H',
          COUNTRY: 'I',
          STATUS: 'J',
          SENT_AT: 'K'
        }
      },
      errors: {},
      loading: false,
      saving: false,
      testResult: null
    };
  },

  mounted() {
    if (this.currentUser.sheetConfig) {
      console.log('Config existante:', this.currentUser.sheetConfig);
      this.config = { ...this.config, ...this.currentUser.sheetConfig };
    }
    
  },

  methods: {
    // 🆕 Copier l'email du Service Account
    copyServiceAccountEmail() {
      navigator.clipboard.writeText(this.serviceAccountEmail).then(() => {
        this.emailCopied = true;
        this.$toast.success('📧 Email copié! Collez-le dans le partage de votre Google Sheet.');
        
        // Reset après 3 secondes
        setTimeout(() => {
          this.emailCopied = false;
        }, 3000);
      }).catch(err => {
        console.error('Erreur copie:', err);
        this.$toast.error('Erreur lors de la copie');
      });
    },

    formatFieldName(key) {
      const names = {
        ORDER_ID: 'Order ID',
        PRODUCT_ID: 'Product ID',
        CUSTOMER_NAME: 'Customer Name',
        PHONE: 'Phone',
        CITY: 'City',
        TOTAL: 'Total',
        QUANTITY: 'Quantity',
        STORE_NAME: 'Store Name',
        COUNTRY: 'Country Code',
        STATUS: 'Status',
        SENT_AT: 'Sent At'
      };
      return names[key] || key;
    },

    isRequiredField(key) {
      return ['ORDER_ID', 'PRODUCT_ID', 'CUSTOMER_NAME', 'PHONE', 'CITY', 'TOTAL', 'QUANTITY', 'COUNTRY'].includes(key);
    },

    getDefaultColumn(key) {
      const defaults = {
        ORDER_ID: 'A',
        PRODUCT_ID: 'B',
        CUSTOMER_NAME: 'C',
        PHONE: 'D',
        CITY: 'E',
        TOTAL: 'F',
        QUANTITY: 'G',
        STORE_NAME: 'H',
        COUNTRY: 'I',
        STATUS: 'J',
        SENT_AT: 'K'
      };
      return defaults[key] || '';
    },

    getExampleValue(key) {
      const examples = {
        ORDER_ID: 'ORD001',
        PRODUCT_ID: 'mhdgdbu5',
        CUSTOMER_NAME: 'John Doe',
        PHONE: '0612345678',
        CITY: 'Casablanca',
        TOTAL: '299',
        QUANTITY: '2',
        STORE_NAME: 'MyStore',
        COUNTRY: 'MA',
        STATUS: 'SENT',
        SENT_AT: '2025-11-01'
      };
      return examples[key] || '...';
    },

    normalizeColumnInput(key) {
      this.config.columnMapping[key] = this.config.columnMapping[key].toUpperCase();
    },

    validate() {
      this.errors = {};
      
      if (!this.config.enabled) {
        return true;
      }

      if (!this.config.sheetId || this.config.sheetId.trim() === '') {
        this.errors.sheetId = 'Le Sheet ID est obligatoire';
      }

      if (!this.config.sheetName || this.config.sheetName.trim() === '') {
        this.errors.sheetName = 'Le nom du sheet est obligatoire';
      }

      const required = ['ORDER_ID', 'PRODUCT_ID', 'CUSTOMER_NAME', 'PHONE', 'CITY', 'TOTAL', 'QUANTITY', 'COUNTRY'];
      
      for (const field of required) {
        if (!this.config.columnMapping[field] || this.config.columnMapping[field].trim() === '') {
          this.errors[`columnMapping.${field}`] = `Obligatoire`;
        }
      }

      return Object.keys(this.errors).length === 0;
    },

    async testConnection() {
      if (!this.validate()) {
        this.$toast.error('Veuillez corriger les erreurs avant de tester');
        return;
      }

      this.loading = true;
      this.testResult = null;

      try {
        const response = await this.$server.update("users", {
          _id: this.currentUser._id,
          'test-connection-sheet': {
            sheetId: this.config.sheetId,
            sheetName: this.config.sheetName,
            startRow: this.config.startRow,
            columnMapping: this.config.columnMapping
          }
        });

        console.log('Test connection response:', response);

        if(
           !response.content ||
           typeof response.content.rowsCount === 'undefined'
        ){
            this.testResult = response.content;
            return false
        }


        

        this.testResult = {
          success: true,
          message: `✅ Connexion réussie! ${response.content.rowsCount || 0} lignes pending détectées.`,
          preview: response.content.preview || []
        };

      } catch (error) {
        console.error('Test connection error:', error);
        this.testResult = {
          success: false,
          message: `❌ Erreur: ${error.message || error}`
        };
      } finally {
        this.loading = false;
      }
    },

    async saveConfig() {
      if (!this.validate()) {
        this.$toast.error('Veuillez corriger les erreurs');
        return;
      }

      this.saving = true;

      try {
        const response = await this.$server.update("users", {
          _id: this.currentUser._id,
          'sheet-config': this.config
        });

        if (!response || (response && !response._id)) {
          this.$toast.error('Erreur lors de la sauvegarde de la configuration: ' + response);
          this.saving = false;
          return;
        }
        
        this.$toast.success('Configuration enregistrée avec succès!');
        this.$emit('config-updated', this.config);
        this.closeModal();

      } catch (error) {
        console.error('Save config error:', error);
        this.$toast.error(`Erreur: ${error.message || error}`);
      } finally {
        this.saving = false;
      }
    },

    formatDate(date) {
      if (!date) return 'Jamais';
      return new Date(date).toLocaleString('fr-FR');
    },

    closeModal() {
      this.$vm2.close('ModalSheetConfig');
    }
  }
};
</script>
<style scoped>
/* Toggle Switch Styles - Compatible Tailwind v1.8 */
.sr-only {
  position: absolute;
  width: 1px;
  height: 1px;
  padding: 0;
  margin: -1px;
  overflow: hidden;
  clip: rect(0, 0, 0, 0);
  white-space: nowrap;
  border-width: 0;
}

.toggle-switch {
  position: relative;
  display: inline-block;
  width: 44px;
  height: 24px;
  background-color: #cbd5e0;
  border-radius: 9999px;
  transition: background-color 0.3s;
}

.toggle-switch:focus {
  outline: none;
  box-shadow: 0 0 0 3px rgba(66, 153, 225, 0.5);
}

.toggle-switch.toggle-active {
  background-color: #4299e1;
}

.toggle-slider {
  position: absolute;
  top: 2px;
  left: 2px;
  width: 20px;
  height: 20px;
  background-color: white;
  border-radius: 9999px;
  transition: transform 0.3s;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
}

.toggle-active .toggle-slider {
  transform: translateX(20px);
}

/* Space utilities */
.space-y-1 > * + * {
  margin-top: 0.25rem;
}

.space-y-2 > * + * {
  margin-top: 0.5rem;
}

.space-y-4 > * + * {
  margin-top: 1rem;
}

.space-x-2 > * + * {
  margin-left: 0.5rem;
}

/* Overflow utilities */
.break-all {
  word-break: break-all;
}

.overflow-x-auto {
  overflow-x: auto;
}
.sr-only {
  position: absolute;
  width: 1px;
  height: 1px;
  padding: 0;
  margin: -1px;
  overflow: hidden;
  clip: rect(0, 0, 0, 0);
  white-space: nowrap;
  border-width: 0;
}

.toggle-switch {
  position: relative;
  display: inline-block;
  width: 44px;
  height: 24px;
  background-color: #cbd5e0;
  border-radius: 9999px;
  transition: background-color 0.3s;
}

.toggle-switch:focus {
  outline: none;
  box-shadow: 0 0 0 3px rgba(66, 153, 225, 0.5);
}

.toggle-switch.toggle-active {
  background-color: #4299e1;
}

.toggle-slider {
  position: absolute;
  top: 2px;
  left: 2px;
  width: 20px;
  height: 20px;
  background-color: white;
  border-radius: 9999px;
  transition: transform 0.3s;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
}

.toggle-active .toggle-slider {
  transform: translateX(20px);
}

/* Space utilities */
.space-y-1 > * + * {
  margin-top: 0.25rem;
}

.space-y-2 > * + * {
  margin-top: 0.5rem;
}

.space-y-4 > * + * {
  margin-top: 1rem;
}

.space-x-2 > * + * {
  margin-left: 0.5rem;
}

/* Overflow utilities */
.break-all {
  word-break: break-all;
}

.overflow-x-auto {
  overflow-x: auto;
}



</style>